import React, { useEffect, useState } from "react";

import { format } from "date-fns";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "~/components/ui/tooltip";
import { useSalesConditions } from "~/hooks/useSalesConditions";
import { supabase } from "~/integrations/supabase/client";
import StatusBadge from "~/pages/clients/components/client-detail/StatusBadge";
import { useProfileStore } from "~/store/profileStore";
import { SalesRequest } from "~/types/client";
import { formatCurrency } from "~/utils/helpers";

interface SalesRequestsTableProps {
  salesRequests: SalesRequest[];
  onRowClick: (salesRequest: SalesRequest) => void;
  clientSalesConditionId?: string;
}

const SalesRequestsTable: React.FC<SalesRequestsTableProps> = ({
  salesRequests,
  onRowClick,
  clientSalesConditionId,
}) => {
  const { getConditionById } = useSalesConditions();
  const { role } = useProfileStore();
  const isSeller = role === "seller";
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [creditNotes, setCreditNotes] = useState<
    Record<string, { amount: number; count: number }>
  >({});

  // Fetch credit notes for sales requests
  useEffect(() => {
    const fetchCreditNotes = async () => {
      if (salesRequests.length === 0) return;

      const salesRequestIds = salesRequests.map((sr) => sr.id);

      try {
        const { data, error } = await supabase
          .from("sales_request_documents")
          .select("sales_request_id, net_amount, tax_amount, total_amount")
          .eq("document_type", "nota_de_credito")
          .in("sales_request_id", salesRequestIds)
          .is("deleted_at", null);

        if (error) throw error;

        // Group by sales request and calculate totals
        const creditNotesMap: Record<
          string,
          { amount: number; count: number }
        > = {};

        if (data) {
          data.forEach((note) => {
            const salesRequestId = note.sales_request_id;
            if (!creditNotesMap[salesRequestId]) {
              creditNotesMap[salesRequestId] = { amount: 0, count: 0 };
            }
            // Use net_amount + tax_amount for total, or fall back to total_amount
            const noteAmount =
              (note.net_amount || 0) + (note.tax_amount || 0) ||
              note.total_amount ||
              0;
            creditNotesMap[salesRequestId].amount += Math.abs(noteAmount);
            creditNotesMap[salesRequestId].count += 1;
          });
        }

        setCreditNotes(creditNotesMap);
      } catch (error) {
        console.error("Error fetching credit notes:", error);
        setCreditNotes({});
      }
    };

    fetchCreditNotes();
  }, [salesRequests]);

  // Listen for resize events
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Helper function to render credit note information
  const renderCreditNote = (salesRequestId: string) => {
    const creditNote = creditNotes[salesRequestId];

    if (!creditNote) {
      return <span className="text-sm text-gray-400">-</span>;
    }

    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger className="cursor-help text-left">
            <div className="text-sm">
              <span className="font-medium text-red-600">
                {formatCurrency(creditNote.amount)}
              </span>
              {creditNote.count > 1 && (
                <span className="ml-1 text-xs text-gray-500">
                  ({creditNote.count})
                </span>
              )}
            </div>
          </TooltipTrigger>
          <TooltipContent>
            {creditNote.count === 1
              ? `Nota de crédito: ${formatCurrency(creditNote.amount)}`
              : `${creditNote.count} notas de crédito por un total de ${formatCurrency(creditNote.amount)}`}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  };

  // Helper function to display sales condition
  const renderSalesCondition = (salesRequest: SalesRequest) => {
    const isInherited = !salesRequest.salesConditionId;
    const conditionId = salesRequest.salesConditionId || clientSalesConditionId;
    const condition = conditionId ? getConditionById(conditionId) : null;

    if (!condition) return "-";

    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger className="cursor-help text-left">
            <span className={isInherited ? "italic text-gray-500" : ""}>
              {condition.name}
              {isInherited && " *"}
            </span>
          </TooltipTrigger>
          <TooltipContent>
            {isInherited
              ? "Heredada del cliente"
              : "Configurada específicamente para esta nota"}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  };

  // Mobile card view for sellers
  if (isSeller && isMobile) {
    return (
      <div className="space-y-3">
        {salesRequests.map((salesRequest) => (
          <div
            key={salesRequest.id}
            className="cursor-pointer rounded-lg border bg-white p-3 shadow-sm"
            onClick={() => onRowClick(salesRequest)}
          >
            <div className="mb-2 flex items-start justify-between">
              <div>
                <p className="text-xs text-gray-500">
                  {salesRequest.factura_number ? (
                    <span className="font-semibold text-blue-600">
                      {salesRequest.factura_number}
                    </span>
                  ) : (
                    <>ID: {salesRequest.id.slice(0, 8)}...</>
                  )}
                </p>
                <p className="text-xs text-gray-500">
                  {format(new Date(salesRequest.createdAt), "dd/MM/yyyy")}
                </p>
              </div>
              <div>
                <StatusBadge status={salesRequest.status} type="order" />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2 pt-2">
              <div>
                <p className="text-xs text-gray-500">Monto:</p>
                <p className="font-semibold">
                  {formatCurrency(
                    salesRequest.priceWithIva || salesRequest.totalAmount
                  )}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Pago:</p>
                <div>
                  {salesRequest.paid ? (
                    <StatusBadge status="cleared" type="payment" />
                  ) : (
                    <StatusBadge status="pending" type="payment" />
                  )}
                </div>
              </div>
            </div>

            <div className="mt-2 border-t border-gray-100 pt-2">
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <p className="text-xs text-gray-500">Condición:</p>
                  <p className="text-sm">
                    {renderSalesCondition(salesRequest)}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Nota Crédito:</p>
                  <div className="text-sm">
                    {renderCreditNote(salesRequest.id)}
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  // Default desktop table view
  return (
    <div className="overflow-hidden rounded-md border">
      <Table>
        <TableHeader className="bg-muted/50">
          <TableRow>
            <TableHead>N° Factura / ID</TableHead>
            <TableHead>Fecha</TableHead>
            <TableHead>Monto</TableHead>
            <TableHead>Condición</TableHead>
            <TableHead>Estado</TableHead>
            <TableHead>Pago</TableHead>
            <TableHead>Nota Crédito</TableHead>
            <TableHead>Tipo</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {salesRequests.map((salesRequest, index) => (
            <TableRow
              key={salesRequest.id}
              className="cursor-pointer duration-300 animate-in fade-in-0 slide-in-from-top-1 fill-mode-both hover:bg-muted/30"
              style={{ animationDelay: `${Math.min(index * 40, 400)}ms` }}
              onClick={() => onRowClick(salesRequest)}
            >
              <TableCell className="font-medium">
                {salesRequest.factura_number ? (
                  <span className="text-sm font-semibold text-blue-600">
                    {salesRequest.factura_number}
                  </span>
                ) : (
                  <span className="text-gray-600">
                    {salesRequest.id.slice(0, 8)}...
                  </span>
                )}
              </TableCell>
              <TableCell>
                {format(new Date(salesRequest.createdAt), "dd/MM/yyyy")}
              </TableCell>
              <TableCell>
                {formatCurrency(
                  salesRequest.priceWithIva || salesRequest.totalAmount
                )}
              </TableCell>
              <TableCell>{renderSalesCondition(salesRequest)}</TableCell>
              <TableCell>
                <StatusBadge status={salesRequest.status} type="order" />
              </TableCell>
              <TableCell>
                {salesRequest.paid ? (
                  <StatusBadge status="cleared" type="payment" />
                ) : (
                  <StatusBadge status="pending" type="payment" />
                )}
              </TableCell>
              <TableCell>{renderCreditNote(salesRequest.id)}</TableCell>
              <TableCell>
                {salesRequest.paymentType === "prepaid" ? "Prepago" : "Crédito"}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

export default SalesRequestsTable;
